/*
 * Copyright (c) 2026-present Amida Technology Solutions, Inc.
 * All rights reserved.
 *
 * Licensed under the Amida proprietary license.
 * See LICENSE file in the project root for license information.
 */

/**
 * Work and records: the two halves of one table.
 *
 * Agreements is the work -- what somebody still has to draft, approve, send or
 * sign. Records is what is finished, and is kept to be looked something up in.
 * They were one list, which put a year of signed contracts beside the three
 * drafts somebody actually had to do something about, and made the list
 * people open every morning the one they had to filter before they could use.
 *
 * The server holds the same split (`PRE_EXECUTION_STAGES` in
 * `core/db/enums.py`). A change here is a change there too.
 *
 * Cancelled was never agreed. Agreements lists it in *All*, beside the work
 * still moving, and alone under a chip of its own (`LIFE-69`); Records not at
 * all.
 */

/** Still moving: the stages Agreements lists and offers a chip for. */
export const IN_PROGRESS_STAGES = [
  'draft',
  'in_approval',
  'ready_to_send',
  'out_for_signature',
] as const;

/**
 * Finished: the stages Records lists.
 *
 * A superseded agreement is still a record. It is what was agreed until the
 * day it was replaced, and somebody will be asked about it. History brought in
 * through the import lands at executed or filed, and is here with the rest.
 */
export const RECORD_STAGES = ['executed', 'filed', 'superseded'] as const;

export type InProgressStage = (typeof IN_PROGRESS_STAGES)[number];

/** Stopped for good: in Agreements' *All*, and under its own chip (`LIFE-69`). */
export const CANCELLED_STAGE = 'cancelled';

/**
 * Every stage each tab of Agreements lists, as one filter: In progress is the
 * work still moving and what was cancelled (`LIFE-69`), Completed every
 * record. The server splits a counterparty's count the same way
 * (`FOLDED_STAGES`, `RECORD_STAGES` in `core/worklist/rows.py`).
 */
export const IN_PROGRESS_TAB = [...IN_PROGRESS_STAGES, CANCELLED_STAGE].join(',');
export const COMPLETED_TAB = RECORD_STAGES.join(',');

/** A stage the In progress tab offers a chip for. */
export type ListedStage = InProgressStage | typeof CANCELLED_STAGE;

/** The stages a `?stage=` filter names. Several, comma separated, are an either/or. */
function named(filter: string | undefined): string[] {
  return (filter ?? '')
    .split(',')
    .map((stage) => stage.trim())
    .filter(Boolean);
}

function isListed(stage: string): stage is ListedStage {
  return (
    stage === CANCELLED_STAGE || (IN_PROGRESS_STAGES as readonly string[]).includes(stage)
  );
}

/**
 * The stages a filter names that Agreements has a chip for -- in progress, or
 * cancelled -- in the order it names them.
 *
 * A finished stage in the filter is dropped rather than obeyed: Agreements
 * does not list them any more, and lighting a chip that no longer exists
 * would leave the list looking mis-filtered.
 */
export function inProgressIn(filter: string | undefined): ListedStage[] {
  return named(filter).filter(isListed);
}

/**
 * Whether a filter asks only for finished agreements.
 *
 * Such a link -- `/agreements?stage=filed`, sent to somebody last month, or
 * a bookmark from before the split -- is asking for Records, and is sent
 * there instead of arriving at a list that cannot show it. A filter that
 * mixes the two is left on Agreements, which shows the half it can.
 */
export function asksOnlyForRecords(filter: string | undefined): boolean {
  const stages = named(filter);
  return (
    stages.length > 0 &&
    stages.every((stage) => (RECORD_STAGES as readonly string[]).includes(stage))
  );
}

/**
 * Signed: what the dashboard counts as signed, and the Completed tab's chips
 * besides Superseded. A superseded agreement was signed, but is not counted
 * as signed in the range it was replaced in.
 */
export const SIGNED_STAGES = ['executed', 'filed'] as const;

/**
 * Every stage there is, in the order the work moves through them.
 *
 * What a figure counted over every stage asks the list for -- the agreements
 * one team or one person started, whatever has become of them -- so the list
 * holds every one the figure counted, cancelled and finished ones included.
 */
export const EVERY_STAGE = [
  ...IN_PROGRESS_STAGES,
  'executed',
  'filed',
  'superseded',
  CANCELLED_STAGE,
] as const;

export type RecordStage = (typeof RECORD_STAGES)[number];

function isRecord(stage: string): stage is RecordStage {
  return (RECORD_STAGES as readonly string[]).includes(stage);
}

/**
 * The finished stages a filter names, in the order it names them.
 *
 * On the In progress tab, a filter that names work and records together --
 * a dashboard figure counted over every stage one team started, say -- keeps
 * both halves: the finished stages it names are asked for too, and each gets
 * a chip of its own, lit, so the list says what it holds. On the Completed
 * tab, the stages it names narrow the records to them.
 */
export function finishedIn(filter: string | undefined): RecordStage[] {
  return named(filter).filter(isRecord);
}
